import { useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

// Pas de week-end (comme le Calendrier du Tableau de bord) : aucun atelier
// n'a jamais lieu samedi/dimanche, les afficher n'apporterait qu'un jour
// cliquable dont la sélection ne servirait jamais à rien.
const JOURS_COURTS = ['Lu', 'Ma', 'Me', 'Je', 'Ve'];

function moisDebut(d: Date): Date { return new Date(d.getFullYear(), d.getMonth(), 1); }
function moisFin(d: Date): Date { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }

export function iso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Toutes les dates ISO entre a et b inclus (ordre corrigé automatiquement). */
function joursEntre(a: string, b: string): string[] {
  const [du, au] = a <= b ? [a, b] : [b, a];
  const debut = new Date(du + 'T00:00:00');
  const fin = new Date(au + 'T00:00:00');
  const out: string[] = [];
  for (let c = debut; c <= fin; c = new Date(c.getFullYear(), c.getMonth(), c.getDate() + 1)) out.push(iso(c));
  return out;
}

/** Grille de semaines complètes (lundi→dimanche) couvrant le mois donné. */
function grilleMois(mois: Date): Date[] {
  const debut = moisDebut(mois);
  const fin = moisFin(mois);
  const decalageDebut = (debut.getDay() + 6) % 7;
  const decalageFin = (7 - ((fin.getDay() + 6) % 7) - 1 + 7) % 7;
  const jours: Date[] = [];
  for (let i = decalageDebut; i > 0; i--) jours.push(new Date(debut.getFullYear(), debut.getMonth(), 1 - i));
  for (let d = 1; d <= fin.getDate(); d++) jours.push(new Date(mois.getFullYear(), mois.getMonth(), d));
  for (let i = 1; i <= decalageFin; i++) jours.push(new Date(fin.getFullYear(), fin.getMonth(), fin.getDate() + i));
  return jours.filter((j) => j.getDay() !== 0 && j.getDay() !== 6);
}

interface MiniCalendrierProps {
  mois: Date;
  onMoisChange: (d: Date) => void;
  /** Jours sélectionnés (sélection individuelle ou plage). */
  selectionnees: Set<string>;
  /** Sous-ensemble de `selectionnees` sans atelier retenu — affiché en plus discret. */
  selectionneesVides?: Set<string>;
  /** Jours portant un point : la personne absente y a une séance programmée. */
  marquees: Set<string>;
  /** Tap simple sur un jour : ajoute/retire ce jour de la sélection. */
  onJourClick: (dateIso: string) => void;
  /** Glissement sur plusieurs jours (souris ou tactile) → plage a→b. */
  onRangeSelect: (dateA: string, dateB: string) => void;
}

// Calendrier bi-mensuel compact, réutilisé dans l'assistant « Nouvelle demande ».
// Deux mois affichés côte à côte (mois affiché + suivant) pour que le
// glissement de sélection puisse franchir une frontière de mois (ex. 28
// juillet → 3 août) sans lâcher le pointeur — les deux grilles partagent le
// même conteneur à écouteurs Pointer Events, donc `elementFromPoint` trouve
// les jours du second mois comme ceux du premier.
// Sélection au tap OU au glissement (souris, tactile iPad, stylet unifiés).
export function MiniCalendrier({ mois, onMoisChange, selectionnees, selectionneesVides, marquees, onJourClick, onRangeSelect }: MiniCalendrierProps) {
  const moisSuivant = new Date(mois.getFullYear(), mois.getMonth() + 1, 1);
  const aujourdHui = iso(new Date());
  const gridRef = useRef<HTMLDivElement>(null);
  const ancreRef = useRef<string | null>(null);
  const [drag, setDrag] = useState<{ ancre: string; courant: string } | null>(null);

  function dateSousPointeur(x: number, y: number): string | null {
    const el = document.elementFromPoint(x, y) as HTMLElement | null;
    const jour = el?.closest('[data-date]') as HTMLElement | null;
    return jour?.dataset.date ?? null;
  }

  function onPointerDown(e: React.PointerEvent) {
    const cible = (e.target as HTMLElement).closest('[data-date]') as HTMLElement | null;
    const d = cible?.dataset.date;
    if (!d) return;
    ancreRef.current = d;
    setDrag({ ancre: d, courant: d });
    gridRef.current?.setPointerCapture(e.pointerId);
  }

  function onPointerMove(e: React.PointerEvent) {
    if (!ancreRef.current) return;
    const d = dateSousPointeur(e.clientX, e.clientY);
    if (!d) return;
    setDrag((prev) => (prev && prev.courant === d ? prev : { ancre: ancreRef.current!, courant: d }));
  }

  function onPointerUp() {
    const ancre = ancreRef.current;
    if (!ancre) return;
    const courant = drag?.courant ?? ancre;
    if (courant !== ancre) onRangeSelect(ancre, courant);
    else onJourClick(ancre);
    ancreRef.current = null;
    setDrag(null);
  }

  const apercu = drag && drag.ancre !== drag.courant ? new Set(joursEntre(drag.ancre, drag.courant)) : null;

  function grille(moisAffiche: Date) {
    return grilleMois(moisAffiche).map((jour) => {
      const dateStr = iso(jour);
      const dansLeMois = jour.getMonth() === moisAffiche.getMonth();
      // Une absence ne se déclare pas pour un jour déjà passé : ces jours
      // sont grisés et non sélectionnables (ni au clic, ni au glisser —
      // pas de data-date). Aujourd'hui reste sélectionnable (dernière minute).
      const estPasse = dateStr < aujourdHui;
      const interactif = dansLeMois && !estPasse;
      const selectionne = selectionnees.has(dateStr);
      const vide = selectionne && (selectionneesVides?.has(dateStr) ?? false);
      const dansApercu = apercu?.has(dateStr) ?? false;
      const marque = marquees.has(dateStr);
      return (
        <div
          key={dateStr}
          data-date={interactif ? dateStr : undefined}
          className="text-sm"
          role={interactif ? 'button' : undefined}
          aria-pressed={interactif ? selectionne : undefined}
          aria-disabled={interactif ? undefined : true}
          title={estPasse && dansLeMois ? 'Date passée' : undefined}
          style={{
            position: 'relative',
            padding: '8px 0',
            textAlign: 'center',
            borderTop: '1px solid var(--line)',
            background: dansApercu ? 'var(--accent-dim)' : vide ? 'var(--accent-dim)' : selectionne ? 'var(--accent)' : 'transparent',
            color: dansApercu || vide ? 'var(--ink-1)' : selectionne ? '#fff' : interactif ? 'var(--ink-1)' : 'var(--ink-3)',
            opacity: interactif ? 1 : 0.35,
            cursor: interactif ? 'pointer' : 'default',
            fontWeight: dateStr === aujourdHui ? 700 : 400,
            textDecoration: estPasse && dansLeMois && !selectionne ? 'line-through' : undefined,
            userSelect: 'none',
          }}
        >
          {jour.getDate()}
          {marque && !selectionne && !dansApercu && interactif && (
            <span aria-hidden style={{
              position: 'absolute', bottom: 3, left: '50%', transform: 'translateX(-50%)',
              width: 4, height: 4, borderRadius: '50%', background: 'var(--accent)',
            }} />
          )}
        </div>
      );
    });
  }

  function panneauMois(moisAffiche: Date, cote: 'gauche' | 'droite') {
    return (
      <div className="card" style={{ padding: 0, overflow: 'hidden', width: 264, flexShrink: 0 }}>
        <div className="flex items-center justify-between" style={{ padding: '8px 12px', borderBottom: '1px solid var(--line)' }}>
          {cote === 'gauche' ? (
            <button type="button" className="btn btn-icon btn-sm" aria-label="Mois précédent"
              onClick={() => onMoisChange(new Date(mois.getFullYear(), mois.getMonth() - 1, 1))}>
              <ChevronLeft className="icon-sm" />
            </button>
          ) : <span style={{ width: 28 }} aria-hidden />}
          <span className="text-sm font-medium text-upper">
            {new Intl.DateTimeFormat('fr-CH', { month: 'long', year: 'numeric' }).format(moisAffiche)}
          </span>
          {cote === 'droite' ? (
            <button type="button" className="btn btn-icon btn-sm" aria-label="Mois suivant"
              onClick={() => onMoisChange(new Date(mois.getFullYear(), mois.getMonth() + 1, 1))}>
              <ChevronRight className="icon-sm" />
            </button>
          ) : <span style={{ width: 28 }} aria-hidden />}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)' }}>
          {JOURS_COURTS.map((j) => (
            <div key={j} className="text-xs text-upper text-muted" style={{ padding: '4px 0', textAlign: 'center' }}>{j}</div>
          ))}
          {grille(moisAffiche)}
        </div>
      </div>
    );
  }

  return (
    <div
      ref={gridRef}
      className="flex gap-8"
      style={{ touchAction: 'none' }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      {panneauMois(mois, 'gauche')}
      {panneauMois(moisSuivant, 'droite')}
    </div>
  );
}
